<template>
    <div
        class="bsdc-config pb-large"
        v-cloak
    >
        <div
            v-if="isLoading"
            class="bsdc:mt-12 bsdc:text-center bsdc:px-5 bsdc:sm:px-[46px] bsdc:lg:px-12"
        >
            <div class="bsdc:mx-auto max-w-large">
                <Loader />
            </div>
        </div>

        <div v-show="!isLoading">
            <Header />

            <div
                v-if="httpExternal && configuratorSettings"
                class="bsdc:px-5 bsdc:sm:px-[46px] bsdc:lg:px-12 bsdc:pt-5 bsdc:sm:pt-10"
            >
                <div class="bsdc:mx-auto max-w-large">
                    <Filters
                        :httpExternal="httpExternal"
                        :configuratorSettings="configuratorSettings"
                        :enabledFilters="[
                            'shape',
                            'price',
                            'color',
                            'clarity',
                            'cut',
                            'availability',
                            'certificate_body',
                            'polish',
                            'symmetry',
                            'fluorescence',
                            'table',
                            'depth',
                            'ratio',
                        ]"
                        :caratMin="caratFilterProps.min"
                        :caratMax="caratFilterProps.max"
                        :caratRange="caratFilterProps.range"
                        :caratDefaultMin="caratFilterProps.defaultMin"
                        :caratDefaultMax="caratFilterProps.defaultMax"
                        :caratStep="caratFilterProps.step"
                        :caratTooltipContent="caratFilterProps.tooltipContent"
                        :typeFilterOptions="typeFilterOptions"
                        :selectedTypeValue="selectedType"
                        :loading="productsLoading"
                        :filtersDirty="filtersDirty"
                        @loaded="handleFiltersLoaded"
                        @changed="handleFiltersChanged"
                        @applyFilters="handleApplyFilters"
                    />
                </div>
            </div>

            <div class="bsdc:space-y-20 bsdc:pt-5 bsdc:sm:pt-10">
                <Section :number="1">
                    <Notice
                        :text="notice"
                        class="bsdc:my-3"
                    />

                    <div
                        v-if="productsLoading"
                        class="bsdc:text-center bsdc:mb-6"
                    >
                        <Loader />
                    </div>

                    <template v-else>
                        <Notice
                            v-if="filtersDirty"
                            class="bsdc:mb-6"
                            type="warning"
                            text="Please apply filters to see updated results."
                        />

                        <template v-if="!filtersDirty">
                            <div v-if="products.lab?.length > 0 || products.natural?.length > 0">
                                <Products
                                    v-if="products.lab.length > 0 && selectedType === 'lab'"
                                    :products="products.lab"
                                    :selectedDiamondId="diamondId"
                                    @selected="selectDiamond"
                                >
                                    <template #end>
                                        <LoadMore
                                            v-if="loadMoreVisibleForLab"
                                            class="bsdc:mt-4"
                                            :loading="loadingMore"
                                            @loadMore="loadMore"
                                        />
                                    </template>
                                </Products>

                                <Products
                                    v-if="products.natural.length > 0 && selectedType === 'natural'"
                                    :products="products.natural"
                                    :selectedDiamondId="diamondId"
                                    @selected="selectDiamond"
                                >
                                    <template #end>
                                        <LoadMore
                                            v-if="loadMoreVisibleForNatural"
                                            class="bsdc:mt-4"
                                            :loading="loadingMore"
                                            @loadMore="loadMore"
                                        />
                                    </template>
                                </Products>
                            </div>

                            <Notice
                                v-else
                                class="bsdc:mb-6"
                                type="error"
                                text='Sorry, no diamonds match your criteria.'
                            />
                        </template>
                    </template>
                </Section>

                <Section
                    v-if="activeSteps.includes(2) && !filtersDirty"
                    :number="2"
                >
                    <Summary
                        v-if="configuratorSettings"
                        :configuratorSettings="configuratorSettings"
                        v-model:ringImageUrl="ringImageUrl"
                        v-model:diamondData="diamondData"
                        v-model:totalPrice="totalPrice"
                        @update:engraving="engraving = $event"
                        @addToCart="addToCart"
                    />
                </Section>
            </div>
        </div>
    </div>
</template>

<script setup>
    import { ref, computed, watch, onBeforeMount, onMounted, onUpdated } from "vue";

    import axios from "axios";

    import { internalApi, externalApi, debounce } from "@js/helpers";

    import Header from "@js/Components/Header.vue";
    import Section from "@js/Components/Section.vue";
    import Products from "@js/Components/Products.vue";
    import LoadMore from "@js/Components/LoadMore.vue";
    import Notice from "@js/Components/Notice.vue";
    import Loader from "@js/Components/Loader.vue";
    import Summary from "@js/Components/Summary.vue";
    import Filters from "@js/Components/Filters.vue";

    const configuratorSettings = ref(null);

    const httpInternal = internalApi();
    const httpExternal = ref(null);

    const configuratorType = computed(() => configuratorSettings.value?.type);
    const types = computed(() => configuratorSettings.value?.types);
    const stones = computed(() => configuratorSettings.value?.stones);
    const allDiamondTypes = ['lab', 'natural'];

    const enabledTypes = computed(() => {
        const configuredTypes = Array.isArray(types.value) ? types.value : [];

        return [...new Set(configuredTypes.filter((type) => allDiamondTypes.includes(type)))];
    });

    const defaultSelectedType = computed(() => enabledTypes.value[0] ?? null);

    const isLoading = ref(true);
    const productsLoading = ref(false);
    const loadMoreVisibleForLab = ref(false);
    const loadMoreVisibleForNatural = ref(false);
    const loadingMore = ref(false);
    const activeSteps = ref([]);
    const notice = ref("");

    const typeCounts = ref({
        lab: 0,
        natural: 0,
    });
    const typeHasMore = ref({
        lab: false,
        natural: false,
    });
    const nextPages = ref({
        lab: 1,
        natural: 1,
    });

    const typeFilterOptions = computed(() => {
        const labels = {
            lab: 'Lab',
            natural: 'Natural',
        };

        return enabledTypes.value.map((type) => {
            const count = typeCounts.value[type].toLocaleString();
            const suffix = typeHasMore.value[type] ? '+' : '';

            return {
                label: `${labels[type]} (${count}${suffix})`,
                value: type,
            };
        });
    });

    const selectedType = computed(() => {
        if (filters.value.type && Array.isArray(filters.value.type) && filters.value.type.length > 0) {
            const currentType = filters.value.type[0];

            if (enabledTypes.value.includes(currentType)) {
                return currentType;
            }
        }

        return defaultSelectedType.value;
    });

    const products = ref({
        lab: [],
        natural: [],
    });

    const filtersLoaded = ref(false);
    const filters = ref({});
    const filtersDirty = ref(false);
    const lastAppliedFilters = ref(null);

    const caratFilterProps = computed(() => {
        const weightFilter = configuratorSettings.value?.filters?.weight;
        const defaultMin = weightFilter?.min || 0.30;
        const defaultMax = weightFilter?.max || 10.0;

        return {
            min: 0.30,
            max: 10.0,
            defaultMin: defaultMin,
            defaultMax: defaultMax,
            range: [defaultMin, defaultMax],
            step: 0.05,
            tooltipContent: configuratorSettings.value?.filterTooltips?.weight || '',
        };
    });

    const productId = ref(null);
    const variationId = ref(null);

    const perPage = 10;
    let diamondsAbortController = null;

    const ringImageUrl = ref("");
    const diamondData = ref([]);
    const diamondId = ref(0);
    const diamondType = ref('');
    const diamondPrice = ref(0);
    const basePrice = ref(0);
    const totalPrice = ref(0);

    const engraving = ref("");
    const engravingPrice = computed(() => configuratorSettings.value?.engravingPrice);

    const filterOverrides = ref({
        filters: configuratorSettings.value?.filter_overrides || {}
    });

    const addEngravingToTotal = () => {
        if (engraving.value) {
            totalPrice.value = totalPrice.value + engravingPrice.value;
        }
    };

    const canonicalStringify = (val) => {
        if (val === null || typeof val !== 'object') {
            return JSON.stringify(val);
        }
        if (Array.isArray(val)) {
            return '[' + val.map(canonicalStringify).join(',') + ']';
        }
        const keys = Object.keys(val).sort();
        return '{' + keys.map((k) => JSON.stringify(k) + ':' + canonicalStringify(val[k])).join(',') + '}';
    };

    const equalFiltersExceptType = (a, b) => {
        const stripType = (o) => {
            if (o == null || typeof o !== 'object') {
                return o;
            }
            const out = Array.isArray(o) ? [...o] : { ...o };
            if (!Array.isArray(out) && 'type' in out) {
                delete out.type;
            }
            return out;
        };
        return canonicalStringify(stripType(a)) === canonicalStringify(stripType(b));
    };

    const handleFiltersLoaded = () => {
        filtersLoaded.value = true;
        filtersDirty.value = false;

        const nextType = defaultSelectedType.value;
        const currentType = filters.value.type?.[0];

        if (nextType && (!currentType || !enabledTypes.value.includes(currentType))) {
            filters.value.type = [nextType];
        }
    };

    const handleFiltersChanged = (newFilters) => {
        filters.value = newFilters;

        if (lastAppliedFilters.value == null) {
            filtersDirty.value = true;
            return;
        }

        filtersDirty.value = !equalFiltersExceptType(lastAppliedFilters.value, newFilters);
    };

    const handleApplyFilters = () => {
        filtersDirty.value = false;
        productsLoading.value = true;
        getDiamonds(true);
    };

    const resetState = () => {
        diamondData.value = [];
        diamondId.value = 0;
        diamondPrice.value = 0;
        totalPrice.value = 0;
        activeSteps.value = [];
    };

    const buildSharedParams = () => {
        const filtersForRequest = { ...filters.value };
        delete filtersForRequest.type;

        return {
            stone: stones.value,
            per_page: perPage,
            ring_weight: configuratorSettings.value?.ringWeight,
            ring_prices: configuratorSettings.value?.ringPrices[variationId.value] ?? [],
            margin_prices: configuratorSettings.value?.marginPrices,
            tiered_prices: configuratorSettings.value?.tieredPrices,
            override_ring_prices: configuratorSettings.value?.overrideRingPrices[variationId.value] ?? [],
            ...filtersForRequest,
            ...filterOverrides.value,
        };
    };

    const normalizeDiamondsResponse = (data) => {
        const diamonds = Array.isArray(data?.diamonds) ? data.diamonds : (Array.isArray(data?.data) ? data.data : []);
        const count = typeof data?.count === 'number' ? data.count : (typeof data?.total === 'number' ? data.total : 0);
        const hasMore = data?.has_more === true;
        const nextPage = Number.isInteger(data?.next_page) ? data.next_page : null;

        return { diamonds, count, hasMore, nextPage };
    };

    const fetchDiamondsForType = async (type, pageNum, signal) => {
        const shared = buildSharedParams();
        const response = await httpExternal.value.get('/diamonds', {
            params: { ...shared, type, page: pageNum },
            signal,
        });
        return normalizeDiamondsResponse(response.data);
    };

    const getDiamonds = async (reset = true) => {
        if (!httpExternal.value) {
            console.error('httpExternal client not initialized');

            return;
        }

        setProductAndVariationIds();

        if (reset) {
            lastAppliedFilters.value = JSON.parse(JSON.stringify(filters.value));
            resetState();
        }

        // Exclude range filters where the min and max are both 0
        Object.entries(filters.value).forEach(([key, filter]) => {
            if (
                filter &&
                typeof filter === 'object' &&
                !Array.isArray(filter) &&
                'min' in filter &&
                'max' in filter &&
                filter.min === 0 &&
                filter.max === 0
            ) {
                delete filters.value[key];
            }
        });

        // Remove min if min is 0 and max is not 0
        if (filters.value.price && filters.value.price.min === 0 && filters.value.price.max !== 0) {
            delete filters.value.price.min;
        }

        // Remove max if max is 0 and min is not 0
        if (filters.value.price && filters.value.price.max === 0 && filters.value.price.min !== 0) {
            delete filters.value.price.max;
        }

        filters.value.final_price = filters.value.price;

        let selectedTypeParam = filters.value.type?.[0] ?? defaultSelectedType.value;

        if (selectedTypeParam && !enabledTypes.value.includes(selectedTypeParam)) {
            selectedTypeParam = defaultSelectedType.value;
        }

        if (reset && selectedTypeParam) {
            filters.value.type = [selectedTypeParam];
        }

        if (diamondsAbortController) {
            diamondsAbortController.abort();
        }
        diamondsAbortController = new AbortController();
        const signal = diamondsAbortController.signal;

        try {
            if (reset) {
                const enabledTypeResults = new Map(await Promise.all(
                    enabledTypes.value.map(async (type) => [type, await fetchDiamondsForType(type, 1, signal)])
                ));

                if (signal.aborted) {
                    return;
                }

                allDiamondTypes.forEach((type) => {
                    const result = enabledTypeResults.get(type);

                    if (!result) {
                        typeCounts.value[type] = 0;
                        typeHasMore.value[type] = false;
                        nextPages.value[type] = 1;
                        products.value[type] = [];

                        return;
                    }

                    typeCounts.value[type] = result.count;
                    typeHasMore.value[type] = result.hasMore;
                    nextPages.value[type] = result.nextPage ?? 1;
                    products.value[type] = addFallbackImagesToDiamonds(result.diamonds);
                });

                if (selectedTypeParam && typeCounts.value[selectedTypeParam] === 0) {
                    const fallbackType = enabledTypes.value.find((type) => typeCounts.value[type] > 0);

                    if (fallbackType) {
                        filters.value.type = [fallbackType];
                    }
                }
            } else {
                if (!selectedTypeParam || !enabledTypes.value.includes(selectedTypeParam)) {
                    return;
                }

                const pageForType = nextPages.value[selectedTypeParam];

                if (!pageForType) {
                    return;
                }

                const { diamonds, hasMore, nextPage } = await fetchDiamondsForType(selectedTypeParam, pageForType, signal);

                if (signal.aborted) {
                    return;
                }

                products.value[selectedTypeParam].push(...addFallbackImagesToDiamonds(diamonds));
                typeCounts.value[selectedTypeParam] = products.value[selectedTypeParam].length;
                typeHasMore.value[selectedTypeParam] = hasMore;
                nextPages.value[selectedTypeParam] = nextPage;
            }

            loadMoreVisibleForLab.value = enabledTypes.value.includes('lab') && typeHasMore.value.lab;
            loadMoreVisibleForNatural.value = enabledTypes.value.includes('natural') && typeHasMore.value.natural;
        } catch (error) {
            if (error?.name === 'CanceledError' || error?.name === 'AbortError') {
                return;
            }
            console.error(error);
            let message = error?.response?.data?.message || 'An error occurred';

            if (message === 'Unauthenticated.') {
                message = 'Sorry, there was an error connecting to the server.';
            }

            notice.value = message;
            filtersDirty.value = true;
        } finally {
            productsLoading.value = false;
            loadingMore.value = false;
        }

        updateActiveSteps();
    };

    const addFallbackImagesToDiamonds = (diamonds) => {
        diamonds.forEach((diamond) => {
            if (!diamond.image_url) {
                diamond.image_url = configuratorSettings.value?.fallbackImages[diamond?.shape?.value];
            }

            if (ringImageUrl.value) {
                diamond.image_urls = diamond.image_urls ?? [];
                diamond.image_urls.push(ringImageUrl.value);
            }

            if (diamond.video_embed) {
                diamond.video_embed_card = diamond.video_embed.replace("500/500", "305/305");
                diamond.video_embed_small = diamond.video_embed.replace("500/500", "378/378");
                diamond.video_embed_large = diamond.video_embed.replace("500/500", "514/514");
            }
        });

        return diamonds;
    };

    const loadMore = () => {
        loadingMore.value = true;

        getDiamonds(false);
    };

    const selectDiamond = (product) => {
        diamondPrice.value = parseFloat(product.setting_price_decimal);
        diamondData.value = product;
        diamondId.value = product.id;
        diamondType.value = product.brand;

        updatePrices();
        updateActiveSteps();

        setTimeout(loadHummWidget, 250);
    };

    const loadRingImage = () => {
        httpInternal.get('/variations/ring-image', {
            params: {
                variation_id: variationId.value,
            },
        })
            .then((response) => {
                const { ring_image_url } = response.data;

                if (ring_image_url) {
                    ringImageUrl.value = ring_image_url;
                }
            })
            .catch((error) => {
                notice.value = error.response.data.message;
            });
    };

    const addDiamondToTotal = () => {
        totalPrice.value = parseFloat(diamondPrice.value);
    };

    const updatePrices = () => {
        totalPrice.value = basePrice.value;

        addDiamondToTotal();
        addEngravingToTotal();
    };

    const updateActiveSteps = () => {
        if (totalPrice.value > 0 && diamondPrice.value > 0 && activeSteps.value.length < 2) {
            activeSteps.value = [1, 2];
        } else if (activeSteps.value.length == 0) {
            activeSteps.value = [1];
        }
    };

    const loadHummWidget = () => {
        const afterSummaryElem = document.querySelector("#summary-widget");

        if (!afterSummaryElem) {
            return;
        }

        afterSummaryElem.innerHTML = "";

        const widgetIds = {
            commins_and_co: "0011n00002TDCWSAA5",
            bespoke_diamonds: "0011n00002ZMNmkAAH",
        };

        const widgetId = widgetIds[configuratorType.value];

        if (!widgetId) {
            return;
        }

        const hummScript = document.createElement("script");
        hummScript.setAttribute("src", `https://d3v2ir16k1una.cloudfront.net/content/scripts/flexifi-widget.js?id=${widgetId}&productPrice=${totalPrice.value}&element=#humm-widget`);

        afterSummaryElem.appendChild(hummScript);
    };

    const addToCart = () => {
        const formData = new FormData();
        formData.append("action", "bsdc_add_to_cart");
        formData.append("productId", productId.value);
        formData.append("variationId", variationId.value);
        formData.append("brand", diamondType.value);
        formData.append("diamondId", diamondId.value);
        formData.append("engraving", engraving.value);

        const variationsData = {};

        document.querySelectorAll('.variations select[name^="attribute_"]').forEach((select) => {
            variationsData[select.name] = select.value;
        });

        formData.append("variationsData", JSON.stringify(variationsData));

        axios.post(BSDC.ajaxAdminUrl, formData)
            .then((response) => {
                const { success, data } = response.data;

                if (success) {
                    window.location.href = data.redirect_url;
                    return;
                }

                alert(data.message || "Something went wrong");
            })
            .catch((error) => {
                console.error(error);
            });
    };

    const setProductAndVariationIds = () => {
        productId.value = document.querySelector('.variations_form input[name="product_id"]')?.value;
        variationId.value = document.querySelector('.single_variation_wrap input[name="variation_id"]')?.value;
    };

    watch(engraving, debounce(() => {
        if (activeSteps.value.includes(2)) {
            loadHummWidget();
        }
    }, 500));

    watch(filtersLoaded, async (loaded) => {
        if (loaded === false) {
            isLoading.value = false;
            return;
        }

        if (loaded === true) {
            setProductAndVariationIds();

            await getDiamonds()
                .finally(() => {
                    isLoading.value = false;

                    loadRingImage();
                });
        }
    });

    watch(engraving, () => {
        updatePrices();
    });

    onBeforeMount(async () => {
        try {
            setProductAndVariationIds();

            await httpInternal.get('/configurator/settings', {
                params: {
                    product_id: productId.value,
                },
            }).then((response) => {
                configuratorSettings.value = response.data.data;

                const weightFilter = configuratorSettings.value?.filters?.weight;
                if (weightFilter) {
                    const min = weightFilter.min || 0;
                    const max = weightFilter.max || 0;

                    if (min !== max) {
                        filters.value.weight = {
                            min: min,
                            max: max,
                        };
                    }
                }
            });

            httpExternal.value = await externalApi();
        } catch (error) {
            notice.value = error.message || error.response?.data?.message || 'Failed to initialize';
            isLoading.value = false;
        }
    });

    onMounted(() => {
        $(document).on("show_variation", async () => {
            if (filtersLoaded.value) {
                isLoading.value = true;

                await getDiamonds()
                    .finally(() => {
                        isLoading.value = false;

                        loadRingImage();
                    });
            }
        });
    });

    onUpdated(() => {
        if (totalPrice.value === 0) {
            totalPrice.value = basePrice.value;
        }

        updateActiveSteps();
    });
</script>
